import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { createQuickbooksIntegration } from './actions/create-quickbooks-integration';
import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';
import { loadOrg } from '../organization/actions/load-org';

import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { ReactComponent as IntegrationInProgressIllustration } from './integration-in-progress-illustration.svg';

import { LoadingMessage } from '../widgets/loading-message';

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    createQuickbooksIntegration: (quickbooksIntegration: QuickbooksIntegrationCreateRequest) =>
      dispatch(createQuickbooksIntegration(quickbooksIntegration)),
    handleClose: () =>
      dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.QUICKBOOKS_CONFIRMATION_DIALOG.key, false)),
    loadOrg: (orgId: number) => dispatch(loadOrg(orgId)),
  },
});

type InputProps = QuickbooksIntegrationAuthResponse;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const QuickbooksConfirmationDialog = connect(
  null,
  mapDispatchToProps,
)((props: Props) => {
  const [isConnecting, setIsConnecting] = useState<boolean>(true);

  // IF (12/30/20): This is a way to help typescript determine which of the two potential types the
  // response is - either QuickbooksIntegrationSuccessfulAuthResponse or
  // QuickbooksIntegrationUnsuccessfulAuthResponse.
  // https://stackoverflow.com/questions/33832475/destructuring-of-union-types-in-typescript
  const isSuccessfulResponse = (
    response: any,
  ): response is QuickbooksIntegrationSuccessfulAuthResponse => !response.error;

  useEffect(() => {
    // IF (12/30/20): The useEffect function can't be async so we need to wrap the logic up in an
    // extra function and immediately invoke it.
    (async () => {
      if (isSuccessfulResponse(props)) {
        const { code, realmId, orgId } = props;
        // IF (01/25/21): We want to make sure the orgs are loaded first so when we create the
        // integration, the org is already in the store and we can update it with the integration
        // info. Otherwise the button doesn't disable properly.
        const parsedOrgId = parseInt(orgId, 10);
        if (!Number.isNaN(parsedOrgId)) {
          await props.actions.loadOrg(parsedOrgId);
          await props.actions.createQuickbooksIntegration({ code, realmId, orgId });
        }
      }
      setIsConnecting(false);
    })();
    // IF (01/19/21): We only want this to fire on the first render so need the dependency array to be empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UiDialog onClose={props.actions.handleClose}>
      <UiDialog.Content>
        {isConnecting && <LoadingMessage />}
        {!isConnecting && isSuccessfulResponse(props) && (
          <div className="u-textAlign-center u-padding-y-xl">
            <div className="typo-h5">You&apos;re Connected!</div>
            <div className="typo-title u-margin-top">
              We&apos;ve started importing your employees and we&apos;ll send you an email when
              we&apos;ve finished. Then any changes you make to your employee list in Quickbooks
              will automatically be reflected in Team Engine.
            </div>
            <IntegrationInProgressIllustration className="u-width-150px u-display-block u-margin-0Auto u-margin-y-lg" />
            <UiButton onClick={props.actions.handleClose}>Continue Using Team Engine</UiButton>
          </div>
        )}
        {!isConnecting && !isSuccessfulResponse(props) && (
          <div className="u-textAlign-center u-padding-y-lg">
            <div className="typo-h5">Something went wrong...</div>
            <div className="typo-title u-margin-y-2x">
              Our engineering team has been alerted to your issue and will be in touch shortly.
              Sorry for the trouble!
            </div>
            <UiButton onClick={props.actions.handleClose}>Continue Using Team Engine</UiButton>
          </div>
        )}
      </UiDialog.Content>
    </UiDialog>
  );
});
